import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Container,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useGeneratorStore } from '../../stores/generatorStore';
import type { GenerationRecord } from './MockData';
import { routes } from '../../routes/routes';

const GenerationHistory: React.FC = () => {
  const navigate = useNavigate();
  const { history } = useGeneratorStore();

  const getStatusIcon = (status: GenerationRecord['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'partial':
        return <WarningIcon color="warning" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: GenerationRecord['status']) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'partial':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    }
    return `${remainingSeconds}s`;
  };

  const formatDateTime = (date: Date): string => {
    return new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Generation History
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => window.location.reload()}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Timetable Name</TableCell>
              <TableCell>Profile</TableCell>
              <TableCell>Start Time</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell align="right">Lessons</TableCell>
              <TableCell align="right">Conflicts</TableCell>
              <TableCell align="right">Hard Rules</TableCell>
              <TableCell align="right">Soft Rules</TableCell>
              <TableCell align="right">Coverage</TableCell>
              <TableCell align="right">Teacher Sat.</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No generation history available. Generate a timetable to see history.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              history.map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(record.status)}
                      <Chip
                        label={record.status}
                        color={getStatusColor(record.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{record.timetableName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {record.profileName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDateTime(record.startTime)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDuration(record.duration)}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{record.lessonsGenerated}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={record.conflictsFound}
                      color={record.conflictsFound === 0 ? 'success' : record.conflictsFound < 15 ? 'warning' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={record.hardRuleViolations}
                      color={record.hardRuleViolations === 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={record.softRuleViolations}
                      color={record.softRuleViolations < 15 ? 'success' : record.softRuleViolations < 25 ? 'warning' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{record.curriculumCoverage.toFixed(1)}%</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{record.teacherSatisfaction}%</Typography>
                  </TableCell>
                  <TableCell>
                    {record.notes && (
                      <Tooltip title={record.notes}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {record.notes}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Timetable">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`${routes.timetable.view}/${record.timetableId}`)}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Stats */}
      {history.length > 0 && (
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="body2" color="text.secondary">
              Total Generations
            </Typography>
            <Typography variant="h5">{history.length}</Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="body2" color="text.secondary">
              Successful
            </Typography>
            <Typography variant="h5" color="success.main">
              {history.filter((r) => r.status === 'success').length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="body2" color="text.secondary">
              Partial
            </Typography>
            <Typography variant="h5" color="warning.main">
              {history.filter((r) => r.status === 'partial').length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="body2" color="text.secondary">
              Failed
            </Typography>
            <Typography variant="h5" color="error.main">
              {history.filter((r) => r.status === 'failed').length}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: '1 1 200px' }}>
            <Typography variant="body2" color="text.secondary">
              Avg. Duration
            </Typography>
            <Typography variant="h5">
              {formatDuration(Math.round(history.reduce((sum, r) => sum + r.duration, 0) / history.length))}
            </Typography>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default GenerationHistory;
